import { getMvpCharacterDefinition, toSpatialDefinition } from '../content/mvp-characters';
import type { CharacterDefinition } from '../content/mvp-characters';
import { BattleCore } from './battle-core';

export const BATTLE_MODES = ['VS_CPU', 'LOCAL_2P'] as const;
export type BattleMode = (typeof BATTLE_MODES)[number];

export interface MvpBattleConfig {
  readonly mode: BattleMode;
  readonly p1CharacterId: string;
  readonly p2CharacterId: string;
  readonly seed: number;
  readonly humanPlayer?: 0 | 1;
}

const uniqueContent = <T extends { readonly id: string }>(
  first: readonly T[],
  second: readonly T[],
): readonly T[] => [...new Map([...first, ...second].map((item) => [item.id, item])).values()];

export const createMvpBattle = (config: MvpBattleConfig): BattleCore => {
  const p1 = getMvpCharacterDefinition(config.p1CharacterId);
  const p2 = getMvpCharacterDefinition(config.p2CharacterId);
  const humanPlayer = config.humanPlayer ?? 0;
  const cpuProfiles = config.mode === 'LOCAL_2P'
    ? [null, null] as const
    : humanPlayer === 0
      ? [null, p2.cpuProfile] as const
      : [p1.cpuProfile, null] as const;
  return new BattleCore({
    seed: config.seed,
    characterIds: ['fighter-p1', 'fighter-p2'],
    characterDefinitionIds: [p1.id, p2.id],
    stage: { minX: 0, maxX: 1_280, groundY: 620 },
    spatialDefinitions: [toSpatialDefinition(p1), toSpatialDefinition(p2)],
    initialPositions: [{ x: 440, y: 620 }, { x: 840, y: 620 }],
    maxHp: [p1.maxHp, p2.maxHp],
    hurtboxes: [p1.hurtBox.standing, p2.hurtBox.standing],
    hurtboxProfiles: [p1.hurtBox, p2.hurtBox],
    attacks: uniqueContent(p1.attacks, p2.attacks),
    attackBindings: [p1.attackBindings, p2.attackBindings],
    airborneAttackBindings: [p1.airborneAttackBindings, p2.airborneAttackBindings],
    trapDefinitions: uniqueContent(p1.trapDefinitions, p2.trapDefinitions),
    cpuProfiles,
  });
};

export const getBattleCharacters = (config: MvpBattleConfig): readonly [CharacterDefinition, CharacterDefinition] =>
  [getMvpCharacterDefinition(config.p1CharacterId), getMvpCharacterDefinition(config.p2CharacterId)];
